import { useMemo } from 'react'
import { Chart } from 'react-chartjs-2'
import type { ChartData, ChartOptions } from 'chart.js'
import { ensureChartsRegistered } from './register'
import { CHART_COLORS, useDarkMode } from './palette'

ensureChartsRegistered()

export interface CategoryScatterChartProps {
  /** y-axis categories (strings), mapped 1:1 with values. */
  labels: string[]
  /** x-axis numeric values, mapped 1:1 with labels. */
  values: number[]
  colors?: readonly string[]
  logarithmic?: boolean
  formatTooltip?: (label: string, value: number) => string
  onPointClick?: (label: string, value: number) => void
  height?: number | string
  className?: string
  ariaLabel?: string
}

/**
 * Horizontal category scatter: y-axis is the category (string), x-axis the
 * numeric value. Matches the legacy `analyze_statements` scatter mode which
 * passes `{x: value, y: label}` points with `scales.y.type='category'`.
 */
export function CategoryScatterChart({
  labels,
  values,
  colors,
  logarithmic = false,
  formatTooltip,
  onPointClick,
  height = 320,
  className,
  ariaLabel,
}: CategoryScatterChartProps) {
  const dark = useDarkMode()

  const data = useMemo<ChartData<'scatter'>>(
    () => ({
      labels,
      datasets: [
        {
          label: 'Value',
          data: values.map((v, i) => ({ x: v, y: labels[i] } as unknown as { x: number; y: number })),
          backgroundColor: colors
            ? labels.map((_, i) => colors[i % colors.length])
            : CHART_COLORS.primary,
          borderWidth: 1.5,
          pointRadius: 6,
          pointHoverRadius: 8,
        },
      ],
    }),
    [labels, values, colors],
  )

  const options = useMemo<ChartOptions<'scatter'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      onClick: onPointClick
        ? (_evt, elements, chart) => {
            if (!elements.length) return
            const el = elements[0]
            const raw = chart.data.datasets[el.datasetIndex]?.data?.[el.index] as unknown
            const point = raw as { x?: number; y?: string } | undefined
            const label = String(point?.y ?? '')
            const value = typeof point?.x === 'number' ? point.x : 0
            onPointClick(label, value)
          }
        : undefined,
      scales: {
        x: {
          type: logarithmic ? 'logarithmic' : 'linear',
          beginAtZero: !logarithmic,
          grid: { color: dark ? CHART_COLORS.gridDark : CHART_COLORS.gridLight },
          ticks: { color: dark ? CHART_COLORS.tickDark : CHART_COLORS.tickLight },
        },
        y: {
          type: 'category',
          labels,
          grid: { display: false },
          ticks: { color: dark ? CHART_COLORS.tickDark : CHART_COLORS.tickLight },
        },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: formatTooltip
            ? {
                label: (ctx) => {
                  const raw = ctx.raw as { x?: number; y?: string }
                  return formatTooltip(String(raw?.y ?? ''), typeof raw?.x === 'number' ? raw.x : 0)
                },
              }
            : undefined,
        },
        datalabels: { display: false },
      },
      animation: { duration: 600 },
    }),
    [dark, logarithmic, labels, formatTooltip, onPointClick],
  )

  return (
    <div
      className={className}
      style={{ height, width: '100%' }}
      role="img"
      aria-label={ariaLabel}
    >
      <Chart type="scatter" data={data} options={options} />
    </div>
  )
}
